import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import {
  DEFAULT_FLOATER,
  FLOATER_PRESETS,
  sendFloater,
  setFloaterCharacter,
  subscribeToFloaterCharacter,
} from '../../services/holidayFloaterService';

/** Master Admin only: sends the holiday character up everyone's screen (who has it on in User Management). */
export const HolidayFloaterButton: React.FC = () => {
  const { currentUser } = useAuth();
  const isMasterAdmin = !!currentUser?.roles?.includes(UserRole.MasterAdmin);
  const [character, setCharacter] = useState(DEFAULT_FLOATER);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMasterAdmin) return;
    return subscribeToFloaterCharacter(setCharacter);
  }, [isMasterAdmin]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPickerOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  if (!currentUser || !isMasterAdmin) return null;

  const send = async () => {
    setBusy(true);
    try {
      await sendFloater(character, currentUser.uid, currentUser.displayName || currentUser.email || 'Unknown');
    } catch (err) {
      console.error('Failed to send holiday character:', err);
      toast.error('Could not send the character');
    } finally {
      setTimeout(() => setBusy(false), 600);
    }
  };

  const choose = async (next: string) => {
    const value = next.trim();
    if (!value) return;
    try {
      await setFloaterCharacter(value);
      setCustom('');
      setPickerOpen(false);
    } catch (err) {
      console.error('Failed to save holiday character:', err);
      toast.error('Could not change the character');
    }
  };

  return (
    <div ref={ref} className="relative flex items-center h-5 rounded-full shadow-md border bg-gray-900/95 border-gray-700">
      <button
        onClick={send}
        disabled={busy}
        className="flex items-center justify-center h-5 pl-1.5 pr-0.5 text-[11px] leading-none disabled:opacity-50"
        title={`Send ${character} up everyone's screen`}
        aria-label="Send holiday character"
      >
        {character}
      </button>
      <button
        onClick={() => setPickerOpen((o) => !o)}
        className="flex items-center justify-center h-5 pr-1 text-gray-300 hover:text-white"
        title="Change the character"
        aria-label="Change holiday character"
      >
        <ChevronDown className="w-2.5 h-2.5" />
      </button>

      {pickerOpen && (
        <div className="absolute left-0 top-6 w-56 bg-gray-900/95 backdrop-blur-sm text-white rounded-xl p-2 shadow-lg border border-gray-700">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Character</div>
          <div className="grid grid-cols-5 gap-1">
            {FLOATER_PRESETS.map((p) => (
              <button
                key={p.character}
                onClick={() => choose(p.character)}
                className={`h-9 rounded-lg text-xl hover:bg-white/15 ${p.character === character ? 'bg-emerald-500/30 ring-1 ring-emerald-400' : 'bg-white/5'}`}
                title={p.label}
              >
                {p.character}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              choose(custom);
            }}
            className="flex gap-1 mt-2"
          >
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              maxLength={8}
              placeholder="Any emoji"
              className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-full px-2 py-1 text-[11px] placeholder:text-gray-500 focus:outline-none focus:border-emerald-400/40"
            />
            <button type="submit" className="px-2 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-[10px] font-semibold">
              Use
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default HolidayFloaterButton;
